import React from 'react';
import { getDateRangePresets } from '../utils/technicalIndicators';
import './ResponsiveStyles.css';

interface DateRangeSelectorProps {
  selectedRange: string;
  maxDate?: Date;
  onRangeChange: (range: string, startDate: Date, endDate: Date) => void;
  theme: 'light' | 'dark';
  customStartDate?: Date;
  customEndDate?: Date;
  onCustomDateChange?: (startDate: Date, endDate: Date) => void;
  selectedPeriodType?: 'Annual Data' | 'Quarterly Data' | 'TTM';
  onPeriodTypeChange?: (type: 'Annual Data' | 'Quarterly Data' | 'TTM') => void;
  isDateRangeLocked?: boolean;
  onDateRangeLockChange?: (locked: boolean) => void;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  selectedRange,
  maxDate,
  onRangeChange,
  theme,
  customStartDate,
  customEndDate,
  onCustomDateChange,
  selectedPeriodType = 'Annual Data',
  onPeriodTypeChange,
  isDateRangeLocked = false,
  onDateRangeLockChange,
}) => {
  const dateRanges = getDateRangePresets();
  
  const periodTypes = [
    { key: 'Annual Data' as const, label: 'Annual' },
    { key: 'Quarterly Data' as const, label: 'Quarterly' },
    { key: 'TTM', label: 'TTM' }
  ];

  const handleRangeClick = (rangeKey: string) => {
    const range = dateRanges[rangeKey as keyof typeof dateRanges];
    if (range) {
      const constrainedEnd = maxDate && range.end > maxDate ? maxDate : range.end;
      onRangeChange(rangeKey, range.start, constrainedEnd);
    }
  };

  const formatDateForInput = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Detect if browser is at 125% zoom
  const isZoomed125 = window.devicePixelRatio >= 1.25;
  const containerGap = isZoomed125 ? '1rem' : '2.2rem';
  const buttonGroupGap = isZoomed125 ? '0rem' : '1.4rem';

  const styles = {
    outerContainer: {
      backgroundColor: theme === 'dark' ? '#111827' : '#ffffff',
      border: `1px solid ${theme === 'dark' ? '#374151' : '#D9D9D9'}`,
      borderRadius: '0.375rem',
      marginBottom: '5px',
      padding: '0rem 0rem',
      height: '4.125rem',
      minHeight: isZoomed125 ? '3.5rem' : '3rem' ,
      maxHeight: isZoomed125 ? '3.5rem' : '3rem' ,
      width: '96%', // Adjust this number to control the width (e.g. 80%, 70%, 60%)
      marginLeft: '0rem',
    },
    container: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      gap: containerGap,
      padding: '0.25rem 0.75rem',
      height: '100%',
      flexWrap: 'wrap' as const,
      fontFamily: 'Manrope, sans-serif',
      fontWeight: '400',
    },
    divider: {
      width: '1px',
      height: '1.5rem',
      backgroundColor: theme === 'dark' ? '#374151' : '#D1D5DB',
      alignSelf: 'center',
    },
    lockDivider: {
      width: '1px',
      height: '1.5rem',
      marginLeft: '1.5rem',
      marginRight: '0.25rem',
      backgroundColor: theme === 'dark' ? '#374151' : '#D1D5DB',
      alignSelf: 'center',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: '400',
      color: theme === 'dark' ? '#D1D5DB' : '#111827',
      marginRight: '8px',
      fontFamily: 'Manrope, sans-serif',
    },
    buttonGroup: {
      display: 'flex',
      gap: buttonGroupGap,
      flexWrap: 'wrap' as const,
    },
    button: {
      padding: '0 0.6rem',
      height: 30,
      fontSize: '0.875rem',
      fontWeight: '400',
      fontFamily: 'Manrope, sans-serif',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      backgroundColor: theme === 'dark' ? '#111827' : '#FFFFFF',
      color: theme === 'dark' ? '#D1D5DB' : '#111827',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: '30px',
    },
    dateRangeButton: {
      padding: '0 0.6rem',
      height: 30,
      fontSize: '0.9375rem',
      fontWeight: '400',
      fontFamily: 'Manrope, sans-serif',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      backgroundColor: theme === 'dark' ? '#111827' : '#FFFFFF',
      color: theme === 'dark' ? '#D1D5DB' : '#111827',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: '30px',
    },
    activeButton: {
      backgroundColor: theme === 'dark' ? '#374151' : '#0D0D0D',
      border: theme === 'dark' ? '1px solid #374151' : '1px solid #0D0D0D',
      color: theme === 'dark' ? '#D1D5DB' : '#FFFFFF',
    },
    customSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      marginLeft: '16px',
      padding: '0.75rem',
      backgroundColor: theme === 'dark' ? '#111827' : '#FFFFFF',
      border: `1px solid ${theme === 'dark' ? '#374151' : '#D1D5DB'}`,
      borderRadius: '0.375rem',
    },
    dateInput: {
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      fontWeight: '500',
      fontFamily: 'Manrope, sans-serif',
      border: `1px solid ${theme === 'dark' ? '#374151' : '#D9D9D9'}`,
      borderRadius: '0.25rem',
      backgroundColor: theme === 'dark' ? '#111827' : '#FFFFFF',
      color: theme === 'dark' ? '#D1D5DB' : '#5A5A5A',
      outline: 'none',
      width: isZoomed125 ? '8rem' : '9rem',
    },
    lockToggle: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.6rem',
      marginLeft: '0.75rem',
      color: theme === 'dark' ? '#D1D5DB' : '#374151',
      fontSize: '0.875rem',
      fontWeight: '500',
      fontFamily: 'Manrope, sans-serif',
      whiteSpace: 'nowrap' as const,
    },
    lockButton: {
      width: '36px',
      height: '22px',
      borderRadius: '16px',
      background: '#F1F7FF',
      border: '1px solid #D9D9D9',
      padding: 0,
      position: 'relative' as const,
      cursor: 'pointer',
    },
    lockSwitch: {
      position: 'relative' as const,
      display: 'block',
      width: '36px',
      height: '22px',
      borderRadius: '999px',
    },
    lockThumb: {
      position: 'absolute' as const,
      top: 0,
      left: isDateRangeLocked ? '14.53px' : '0.42px',
      width: '19.058822631835938px',
      height: '19.161291122436523px',
      borderRadius: '50%',
      background: '#2F58F2',
      boxShadow: '0px 3px 7px 0px #0000001F',
      transition: 'left 160ms ease',
    },
    customButton: {
      padding: '0.5rem 1rem',
      fontSize: '0.875rem',
      fontWeight: '500',
      fontFamily: 'Manrope, sans-serif',
      backgroundColor: theme === 'dark' ? '#10B981' : '#059669',
      color: '#FFFFFF',
      border: 'none',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      fontSize: '0.875rem',
      fontWeight: '500',
      fontFamily: 'Manrope, sans-serif',
      backgroundColor: theme === 'dark' ? '#6B7280' : '#9CA3AF',
      color: '#FFFFFF',
      border: 'none',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
    },
  };

  return (
    <div style={styles.outerContainer}>
      <div className="date-range-container" style={styles.container}>
        {/* Period Type Buttons */}
        {onPeriodTypeChange && (
            <div style={styles.buttonGroup}>
              {periodTypes.map((period) => (
                <button
                  key={period.key}
                  style={{
                    ...styles.button,
                    ...(selectedPeriodType === period.key ? styles.activeButton : {}),
                  }}
                  onClick={() => {
                    onPeriodTypeChange(period.key as any);
                  }}
                  onMouseEnter={(e) => {
                    if (selectedPeriodType !== period.key) {
                      e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4B5563' : '#F3F4F6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedPeriodType !== period.key) {
                      e.currentTarget.style.backgroundColor = theme === 'dark' ? '#111827' : '#FFFFFF';
                    }
                  }}
                >
                  {period.label}
                </button>
              ))}
            </div>
          )}
          
          {/* Date Range Buttons */}
          <div style={styles.buttonGroup}>
            {Object.entries(dateRanges).map(([key, range], index) => (
              <React.Fragment key={key}>
                {index === 0 && <div style={styles.divider} />}
                <button
                  style={{
                    ...styles.dateRangeButton,
                    ...(selectedRange === key ? styles.activeButton : {}),
                    cursor: 'pointer',
                  }}
                  onClick={() => handleRangeClick(key)}
                  onMouseEnter={(e) => {
                    if (selectedRange !== key) {
                      e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4B5563' : '#F3F4F6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedRange !== key) {
                      e.currentTarget.style.backgroundColor = theme === 'dark' ? '#111827' : '#FFFFFF';
                    }
                  }}
                >
                  {range.label}
                </button>
              </React.Fragment>
            ))}
          </div>
          
          {/* Divider before date inputs */}
          <div style={styles.divider} />

          {/* Date inputs + Create Price Alert (grouped to reduce spacing) */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isZoomed125 ? '0.35rem' : '0.5rem',
            }}
          >
            <div id="charts-custom-start-date" style={{ display: 'flex' }}>
              <input
                type="date"
                style={{ ...styles.dateInput, cursor: 'pointer' }}
                max={maxDate ? formatDateForInput(maxDate) : undefined}
                value={customStartDate ? formatDateForInput(customStartDate) : ''}
                onChange={(e) => {
                  if (onCustomDateChange && customEndDate) {
                    onCustomDateChange(new Date(e.target.value), customEndDate);
                    onRangeChange('CUSTOM', new Date(e.target.value), customEndDate);
                  }
                }}
              />
            </div>

            <div id="charts-custom-end-date" style={{ display: 'flex' }}>
              <input
                type="date"
                style={{ ...styles.dateInput, cursor: 'pointer' }}
                max={maxDate ? formatDateForInput(maxDate) : undefined}
                value={customEndDate ? formatDateForInput(customEndDate) : ''}
                onChange={(e) => {
                  if (onCustomDateChange && customStartDate) {
                    onCustomDateChange(customStartDate, new Date(e.target.value));
                    onRangeChange('CUSTOM', customStartDate, new Date(e.target.value));
                  }
                }}
              />
            </div>
            <div style={styles.lockDivider} aria-hidden="true" />
            <div style={styles.lockToggle}>
              <span>Unlock</span>
              <button
                type="button"
                role="switch"
                aria-checked={isDateRangeLocked}
                aria-label="Toggle date range lock"
                title={isDateRangeLocked ? 'Unlock date range' : 'Lock date range'}
                style={styles.lockButton}
                onClick={() => onDateRangeLockChange?.(!isDateRangeLocked)}
              >
                <span style={styles.lockSwitch} aria-hidden="true">
                  <span style={styles.lockThumb} />
                </span>
              </button>
              <span>Lock</span>
            </div>
          </div>
      </div>
    </div>
  );
};
